<template>
	<v-row>
		<v-col>

			<ui-box new-forms>
				<form-title title="Cart Installation">
					Copy the snippet and paste in the head section of your website, or on each page
					you wish Bandwango elements to appear. Paste exactly as-is.
					<br>
					Review the embed code instructions below.
				</form-title>

				<v-row>
					<v-col class="text-left" cols="auto">
						<v-btn
								class="rounded-lg"
								color="info"
								depressed
								@click.prevent="openViewInstructions"
						>
							<v-icon>
								mdi-eye-outline
							</v-icon>
							View Instructions
						</v-btn>
					</v-col>
				</v-row>

				<form-divider inner/>

				<v-row>
					<v-col>
						<div class="align-center field-actions d-flex justify-space-between">
							<v-switch
									v-model="hideCartTab"
									class="my-0"
									dense
									hide-details="auto"
									inset
									label="Hide Cart Tab"
							></v-switch>
							<v-btn icon @click="copyToClipboard(cartEmbedCode)">
								<v-icon color="secondary">mdi-content-copy</v-icon>
							</v-btn>
						</div>
						<v-textarea
								v-model="cartEmbedCode"
								class="rounded-lg"
								label="Code Snippet"
								outlined
								persistent-hint
								readonly
								rows="4"
						></v-textarea>
					</v-col>
				</v-row>
			</ui-box>

			<form-divider/>

			<v-form
					v-model="formIsValid.elementsForm"
					ref="elementsForm"
					id="embedded"
					new-forms
			>
				<ui-box :form="$refs.elementsForm" new-forms>
					<form-title title="Web Page Elements">
						Create and edit the modules that will placed on your website.
						Drag and Drop to rearrange the order of the modules.
						<template v-slot:button>
							<v-btn
									class="rounded-lg"
									color="info"
									depressed
									outlined
									small
									@click="selectModule"
							>
								<v-icon>
									mdi-plus
								</v-icon>
								Add Module
							</v-btn>
						</template>
					</form-title>

					<form-divider inner/>

					<v-row justify="center">
						<v-col>
							<div>
								<v-container>
									<draggable
											v-model="channel.modules"
											group="elements"
											class="my-n4"
											direction="vertical"
											filter="input"
											tag="div"
											@start="onDragStart"
											@end="onDragEnd"
											@change="handleModulesDrop"
									>
										<transition-group
												type="transition"
												:name="!drag ? 'flip-list' : null"
										>
											<template
													v-for="(module, index) in channel.modules"
											>
												<v-row no-gutters
															 v-if="!module.deleted"
															 :key="index"
												>
													<v-col>
														<v-card class="d-flex flex-row align-center py-2">
															<v-row align="center" no-gutters>
																<v-col cols="auto" class="d-flex align-center">
																	<v-icon>
																		mdi-drag-vertical
																	</v-icon>
																</v-col>
																<v-col>
																	{{ module.name }}
																</v-col>
																<v-col>
																	{{ module.settings?.notes }}
																</v-col>
																<v-col cols="auto" class="me-2">
																	<v-btn
																			color="secondary"
																			depressed
																			fab
																			outlined
																			small
																			@click="editModule(index)"
																	>
																		<v-icon>
																			mdi-pencil-outline
																		</v-icon>
																	</v-btn>
																</v-col>
																<v-col cols="auto" class="me-2">
																	<v-btn
																			color="secondary"
																			depressed
																			fab
																			outlined
																			small
																			@click="copyToClipboard(getModuleHtmlText(module.id))"
																	>
																		<v-icon>
																			mdi-content-copy
																		</v-icon>
																	</v-btn>
																</v-col>
																<v-col cols="auto" class="me-5">
																	<v-btn
																			color="error"
																			depressed
																			fab
																			outlined
																			small
																			@click="removeModule(index)"
																	>
																		<v-icon>
																			mdi-trash-can-outline
																		</v-icon>
																	</v-btn>
																</v-col>
															</v-row>
														</v-card>
													</v-col>
												</v-row>
											</template>
										</transition-group>
									</draggable>
									<!-- Hidden Text Field for validation -->
									<div style="display: none;">
										<v-text-field
												v-model="channel.modules"
												:rules="rules.modules"
												hidden
										></v-text-field>
									</div>
								</v-container>
							</div>
						</v-col>
					</v-row>
				</ui-box>

				<form-divider/>

				<ui-box
						new-forms
				>
					<v-row class="mb-5">
						<v-col class="text-right">
							<v-btn
									class="rounded-lg"
									color="info"
									depressed
									@click.prevent="openViewChannel"
							>
								<v-icon>
									mdi-eye-outline
								</v-icon>
								Preview Channel
							</v-btn>
						</v-col>
					</v-row>
				</ui-box>

				<save-form
						:refs="$refs"
						:is-saving="isSaving"
						new-forms
						save-text="Save"
						@cancel-action="cancelChanges"
						@save-action="saveChanges"
				></save-form>
				<confirm-dialog
						:open="removeModuleDialog"
						description="Are you sure? This can't be undone."
						title="Remove Element"
						@cancel="cancelRemoveModule"
						@confirm="doRemoveModule"
				></confirm-dialog>

				<v-dialog
						v-if="dialog.showEditingModule"
						content-class="rounded-xl"
						persistent
						scrollable
						:value="true"
						width="800px"
				>
					<v-form
							v-model="formIsValid.editingModule"
							ref="editingModuleForm"
							id="module"
					>
						<v-card class="position-relative" rounded="xl">
							<v-card-title class="d-flex justify-space-between ma-0 ma-md-4">
								<div class="w-100">
									<div class="align-center d-flex justify-space-between">
										<h6>Edit Module {{ editingModule.name }}</h6>
										<v-btn
												class="mt-n1 me-n2"
												color="black"
												depressed
												icon
												@click="dialog.showEditingModule = false;"
										>
											<v-icon>mdi-close</v-icon>
										</v-btn>
									</div>
								</div>
							</v-card-title>

							<v-card-text class="text-body-2">
								<v-row>
									<v-col>
										<v-text-field
												v-model="editingModule.settings.notes"
												class="rounded-lg"
												hint="Add a label to quickly identify module contents"
												label="Label"
												outlined
												persistent-hint
												type="text"
										></v-text-field>
									</v-col>
								</v-row>

								<!-- These types should go into its own vue files if they keep growing -->
								<template v-if="editingModule.type === channel.const.TYPE_ITEM_CARDS">
									<v-row>
										<v-col cols="6">
											<v-row no-gutters>
												<v-col>
													<span>Hide</span>
												</v-col>
											</v-row>
											<v-row no-gutters>
												<v-col>
													<ui-box
															style="min-height: 300px"
															new-forms
													>
														<v-container>
															<draggable
																	v-model="editingModule.selectableItems"
																	group="elements"
																	class="my-n4 sortables"
																	direction="vertical"
																	filter="input"
																	tag="div"
																	@start="onDragStart"
																	@end="onDragEnd"
															>
																<transition-group
																		type="transition"
																		:name="!drag ? 'flip-list' : null"
																>
																	<v-row
																			v-for="selectableItem in editingModule.selectableItems"
																			:key="selectableItem.id"
																			no-gutters
																			class="mb-2"
																	>
																		<v-col>
																			<v-card class="d-flex flex-row align-center py-2">
																				<v-row align="center" no-gutters>
																					<v-col cols="auto" class="d-flex align-center">
																						<v-icon>
																							mdi-drag-vertical
																						</v-icon>
																					</v-col>
																					<v-col>
																						{{ selectableItem.name }}
																					</v-col>
																				</v-row>
																			</v-card>
																		</v-col>
																	</v-row>
																</transition-group>
															</draggable>
														</v-container>
													</ui-box>
												</v-col>
											</v-row>
										</v-col>
										<v-col cols="6">
											<v-row no-gutters>
												<v-col>
													<span>Show</span>
												</v-col>
											</v-row>
											<v-row no-gutters>
												<v-col>
													<ui-box
															style="min-height: 300px"
															new-forms
													>
														<v-container>
															<draggable
																	v-model="editingModule.selectedItems"
																	group="elements"
																	class="my-n4 sortables"
																	direction="vertical"
																	filter="input"
																	tag="div"
																	@start="onDragStart"
																	@end="onDragEnd"
															>
																<transition-group
																		type="transition"
																		:name="!drag ? 'flip-list' : null"
																>
																	<v-row
																			v-for="selectedItem in editingModule.selectedItems"
																			:key="selectedItem.id"
																			no-gutters
																			class="mb-2"
																	>
																		<v-col>
																			<v-card class="d-flex flex-row align-center py-2">
																				<v-row align="center" no-gutters>
																					<v-col cols="auto" class="d-flex align-center">
																						<v-icon>
																							mdi-drag-vertical
																						</v-icon>
																					</v-col>
																					<v-col>
																						<template v-if="selectedItem.venueName">{{ selectedItem.venueName }} - </template>{{ selectedItem.name }}
																					</v-col>
																				</v-row>
																				
																			</v-card>
																		</v-col>
																	</v-row>
																</transition-group>
															</draggable>
														</v-container>
													</ui-box>
												</v-col>
											</v-row>
										</v-col>
									</v-row>

									<v-row>
										<v-col>
											<div class="align-center field-actions d-flex justify-end">
												<v-btn
														icon
														:disabled="!editingModule.id"
														@click="copyToClipboard(editingModule.moduleHtmlText)"
												>
													<v-icon color="secondary">mdi-content-copy</v-icon>
												</v-btn>
											</div>
											<v-text-field
													v-model="editingModule.moduleHtmlText"
													class="rounded-lg"
													label="HTML Code"
													outlined
													hide-details
													readonly
													type="text"
											></v-text-field>
										</v-col>
									</v-row>
								</template>


								<template
										v-if="editingModule.type === channel.const.TYPE_CHECKOUT_HOW_IT_WORKS">
									<v-row>
										<v-col>
											<div class="align-center field-actions d-flex justify-end">
												<v-btn
														icon
														:disabled="!editingModule.id"
														@click="copyToClipboard(editingModule.moduleHtmlText)"
												>
													<v-icon color="secondary">mdi-content-copy</v-icon>
												</v-btn>
											</div>
											<v-text-field
													v-model="editingModule.moduleHtmlText"
													class="rounded-lg"
													label="HTML Code"
													outlined
													hide-details
													readonly
													type="text"
											></v-text-field>
										</v-col>
									</v-row>

									<v-row>
										<v-col>
											<v-text-field
													v-model="editingModule.settings.title"
													class="rounded-lg"
													label="How it Works Title"
													outlined
													type="text"
											></v-text-field>
										</v-col>
									</v-row>


									<template v-for="(step, index) in editingModule.settings?.steps">
										<v-row>
											<v-col cols="auto" class="align-self-center">
												<img :src="step.png" style="max-height: 40px" :alt="step.label">
											</v-col>
											<v-col class="align-self-center">
												<h6>Step {{ index + 1 }}</h6>
											</v-col>
										</v-row>
										<v-row>
											<v-col>
												<v-text-field
														v-model="step.label"
														class="rounded-lg"
														:label="`Step ${index + 1} Label`"
														outlined
														type="text"
												></v-text-field>
											</v-col>
										</v-row>
										<v-row>
											<v-col>
												<v-textarea
														v-model="step.desc"
														class="rounded-lg"
														:label="`Step ${index + 1} Description`"
														outlined
														rows="4"
												></v-textarea>
											</v-col>
										</v-row>
									</template>
								</template>

							</v-card-text>

							<v-card-actions>
								<div class="align-center d-flex justify-space-between w-100">
									<span></span>
									<div class="d-flex justify-end">
										<v-btn
												class="me-2 me-md-4 rounded-lg"
												color="grey darken-1"
												depressed
												:large="$vuetify.breakpoint.mdAndUp"
												outlined
												@click="hideAndCleanEditingModule"
										>
											Cancel
										</v-btn>
										<v-btn
												class="rounded-lg"
												color="primary"
												depressed
												:large="$vuetify.breakpoint.mdAndUp"
												@click="saveEditingModule"
										>
											Save Module
										</v-btn>
									</div>
								</div>
							</v-card-actions>
						</v-card>
					</v-form>
				</v-dialog>

				<v-dialog
						v-if="dialog.showViewChannel"
						content-class="rounded-xl"
						persistent
						scrollable
						:value="true"
						width="1200px"
				>
					<v-card class="position-relative" rounded="xl">
						<v-card-title class="d-flex justify-space-between ma-0 ma-md-4">
							<div class="w-100">
								<div class="align-center d-flex justify-space-between">
									<div class="align-center d-flex">
										<h6>Preview Channel</h6>

										<v-btn
												class="ml-3"
												color="primary"
												depressed
												icon
												@click="viewChannelAsDesktop"
										>
											<v-icon>mdi-laptop</v-icon>
										</v-btn>
										<v-btn
												class="ml-1"
												color="primary"
												depressed
												icon
												@click="viewChannelAsMobile"
										>
											<v-icon>mdi-cellphone</v-icon>
										</v-btn>
									</div>
									<v-btn
											class="mt-n1 me-n2"
											color="black"
											depressed
											icon
											@click="dialog.showViewChannel = false;"
									>
										<v-icon>mdi-close</v-icon>
									</v-btn>
								</div>
							</div>
						</v-card-title>
						<v-card-text class="text-body-2">
							<div class="d-flex flex-fill justify-content-center mt-3">
								<iframe :src="`${channel.editUrl}&preview=true`"
												:width="viewChannel.width"
												:height="viewChannel.height"
								></iframe>
							</div>
						</v-card-text>

						<v-card-actions>
							<div class="align-center d-flex justify-space-between w-100">
								<span></span>
								<div class="d-flex justify-end">
									<v-btn
											class="me-2 me-md-4 rounded-lg"
											color="grey darken-1"
											depressed
											:large="$vuetify.breakpoint.mdAndUp"
											outlined
											@click="hideViewChannel"
									>
										Close
									</v-btn>
								</div>
							</div>
						</v-card-actions>
					</v-card>
				</v-dialog>

				<v-dialog
						v-if="dialog.showSelectModule"
						content-class="rounded-xl"
						persistent
						scrollable
						:value="true"
						width="800px"
				>
					<v-form
							v-model="formIsValid.selectModule"
							ref="selectModuleForm"
							id="module"
					>
						<v-card class="position-relative" rounded="xl">
							<v-card-title class="d-flex justify-space-between ma-0 ma-md-4">
								<div class="w-100">
									<div class="align-center d-flex justify-space-between">
										<h6>Select Module</h6>
										<v-btn
												class="mt-n1 me-n2"
												color="black"
												depressed
												icon
												@click="dialog.showSelectModule = false;"
										>
											<v-icon>mdi-close</v-icon>
										</v-btn>
									</div>
								</div>
							</v-card-title>

							<v-card-text class="text-body-2">
								<v-row>
									<v-col>
										<v-select
												v-model="selectedModule"
												:items="extraData.selectableModules"
												class="rounded-lg"
												:dense="$vuetify.breakpoint.smAndDown"
												hide-details
												item-text="name"
												item-value="id"
												label="Select a Module"
												outlined
										></v-select>
									</v-col>
								</v-row>
							</v-card-text>

							<v-card-actions>
								<div class="align-center d-flex justify-space-between w-100">
									<span></span>
									<div class="d-flex justify-end">
										<v-btn
												class="me-2 me-md-4 rounded-lg"
												color="grey darken-1"
												depressed
												:large="$vuetify.breakpoint.mdAndUp"
												outlined
												@click="hideAndCleanSelectModule"
										>
											Cancel
										</v-btn>
										<v-btn
												class="rounded-lg"
												color="primary"
												depressed
												:large="$vuetify.breakpoint.mdAndUp"
												@click="addSelectedModule"
										>
											Add Module
										</v-btn>
									</div>
								</div>
							</v-card-actions>
						</v-card>
					</v-form>
				</v-dialog>

				<bw-modal
						v-if="dialog.showViewInstructions"
						title="Embed Code Instructions"
						:width="1200"
				>
					<template #body>
						<div class="d-flex flex-fill justify-content-center mt-3">
							<template v-if="isViewInstructionsLoading">
								<v-row justify="center" align="center">
									<v-col cols="4" class="d-flex justify-center">
										<v-progress-circular
												indeterminate
												color="primary"
												size="50"
										></v-progress-circular>
									</v-col>
								</v-row>
							</template>
							<template v-if="viewInstructions">
								<div v-html="viewInstructions"></div>
							</template>
						</div>
					</template>
					<template #footer>
						<div class="align-center d-flex justify-space-between w-100">
							<span></span>
							<div class="d-flex justify-end">
								<v-btn
										class="me-2 me-md-4 rounded-lg"
										color="grey darken-1"
										depressed
										:large="$vuetify.breakpoint.mdAndUp"
										text
										@click="hideViewInstructions"
								>
									Cancel
								</v-btn>
							</div>
						</div>
					</template>
				</bw-modal>

			</v-form>
		</v-col>
	</v-row>
</template>

<script>
import draggable from 'vuedraggable';
import ConfirmDialog from '@app/Shared/UI/ConfirmDialog.vue';
import { mapActions } from 'vuex';
import { copyToClipboard, helpers } from '../../../../Shared/helpers';

export default {
	name: 'ChannelHtmlCodes',
	components: {
		ConfirmDialog,
		draggable
	},
	props: {
		channel: {
			type: Object,
			default: () => ({})
		},
		extraData: {
			type: Object,
			default: () => ({})
		},
		partner: {
			type: Object | null,
			default: null
		},
		returnUrl: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			dialog: {
				showEditingModule: false,
				showViewChannel: false,
				showSelectModule: false,
				showViewInstructions: false,
			},
			formIsValid: {
				elementsForm: false,
				editingModule: false,
				selectModule: false,
			},
			hideCartTab: true,
			isSaving: false,
			drag: false,
			editingModule: null,
			selectedModule: null,
			removeModuleIndex: false,
			viewChannel: {
				width: "100%",
				height: "667px"
			},
			rules: {
				modules: [v => v && v.length > 0 || 'A web page element is required'],
			},
			viewInstructions: null,
			isViewInstructionsLoading: false
		}
	},
	computed: {
		cartEmbedCode() {
			return this.hideCartTab
					? this.extraData.cartEmbedCodeOpenCartHidden
					: this.extraData.cartEmbedCodeOpenCartShown;
		},
		removeModuleDialog: {
			get() {
				return this.removeModuleIndex !== false;
			},
			set(v) {
				this.removeModuleIndex = v;
			}
		},
		downloadInstructionsUrl() {
			return `/partner-portal/${this.partner.id}/channels/get-embed-instructions-pdf/${this.channel.id}?hideCartTab=${!!this.hideCartTab}`;
		},
	},
	methods: {
		...mapActions('formState', ['setDirtyState']),
		copyToClipboard,
		onDragStart() {
			this.drag = true;
		},
		onDragEnd() {
			this.drag = false;
		},
		handleModulesDrop() {
			this.sortModules();
		},
		sortModules() {
			if(this.channel.modules?.length > 0) {
				let sortOrder = 0;
				this.channel.modules.forEach(module => {
					module.sort_order = sortOrder++;
				});
			}
		},
		editModule(index) {
			this.dialog.showEditingModule = true;
			this.editingModule = new EditModule(index, this.channel, this.extraData, this.getModuleHtmlText);
		},
		hideAndCleanEditingModule() {
			this.dialog.showEditingModule = false;
			this.editingModule = null;
		},
		hideAndCleanSelectModule() {
			this.dialog.showSelectModule = false;
		},
		selectModule() {
			this.dialog.showSelectModule = true;
		},
		addSelectedModule() {
			if(!this.selectedModule) {
				this.hideAndCleanSelectModule();
				return;
			}
			const selectedModule = this.extraData.selectableModules.find(module => module.id === this.selectedModule);
			const newModule = {
				id: null,
				settings: {},
				sort_order: this.channel.modules?.length ?? 0,
				module_id: selectedModule.id,
				assignment: 'offer',
				assignment_id: this.channel.id,
				offer_id: this.channel.id,
				name: selectedModule.name,
				type: selectedModule.type
			};
			this.channel.modules.push(newModule);

			this.isSaving = true;
			let url = `/partner-portal/${this.partner.id}/channels/save-channel/${this.channel.id || ''}`;
			axios.put(url, this.channel).then(response => {
				this.$set(this.channel, 'modules', response.data.channel.modules);
				this.isSaving = false;
				this.selectedModule = null;
				this.hideAndCleanSelectModule();
			}).catch(error => {
				this.isSaving = false;
				window.EventBus.$emit('system-messages', [
					{ message: error.message, messageType: 'error' }
				]);
			});
		},
		openViewInstructions() {
			this.dialog.showViewInstructions = true;
			this.isViewInstructionsLoading = true;
			const url = `/partner-portal/${this.partner.id}/channels/get-embed-instructions/${this.channel.id || ''}?hideCartTab=${!!this.hideCartTab}`;
			axios.get(url)
					.then(response => {
						this.viewInstructions = response.data;
					})
					.catch(error => {
						window.EventBus.$emit(
								'system-messages',
								[{ message: error.message, messageType: 'error' }]
						);
					})
					.finally(() => {
						this.isViewInstructionsLoading = false;
					});
		},
		openViewChannel() {
			this.dialog.showViewChannel = true;
		},
		viewChannelAsDesktop() {
			this.viewChannel.width = "100%";
		},
		viewChannelAsMobile() {
			this.viewChannel.width = "340px";
		},
		hideViewChannel() {
			this.dialog.showViewChannel = false;
		},
		hideViewInstructions() {
			this.dialog.showViewInstructions = false;
			this.viewInstructions = null;
		},
		saveEditingModule() {
			if(this.editingModule.type === this.channel.const.TYPE_ITEM_CARDS) {
				this.editingModule.settings.sort_order_type_ids = this.editingModule.selectedItems.map(item => ({
					type: item.type,
					type_id: item.id
				}));
				this.editingModule.settings.total_displayed = this.editingModule.selectedItems.length;
			}
			this.$set(this.channel.modules[this.editingModule.editIndex], 'settings', JSON.parse(JSON.stringify(this.editingModule.settings)));
			this.saveChanges();
			this.dialog.showEditingModule = false;
			this.editingModule = null;

		},
		removeModule(index) {
			this.removeModuleDialog = index;
		},
		cancelRemoveModule() {
			this.removeModuleDialog = false;
		},
		doRemoveModule() {
			this.$set(this.channel.modules[this.removeModuleIndex], 'deleted', true);
			this.sortModules();
			this.saveChanges();
			this.removeModuleDialog = false;
		},
		cancelChanges() {
			window.location.href = `/partner-portal/${this.partner.id}/channels`;
		},
		getModuleHtmlText(moduleId) {
			return moduleId
					? `<div class="bwmodule" data-bwmoduleid="${moduleId}"></div>`
					: 'HTML code will become visible after the page is saved';
		},
		saveChanges() {
			// Validate first...
			this.$refs.elementsForm.validate();
			if(!this.formIsValid.elementsForm) return;

			this.isSaving = true;
			let url = `/partner-portal/${this.partner.id}/channels/save-channel/${this.channel.id || ''}`;
			axios.put(url, this.channel).then(response => {
				this.$set(this.channel, 'id', response.data.channel.id);
				this.$set(this.channel, 'modules', response.data.channel.modules);
				this.isSaving = false;
				window.EventBus.$emit('system-messages', [
					{
						message: this.channel.name + ' ' + (this.channel.id
								? 'updated'
								: 'created'),
						messageType: 'success',
						showOverlay: true,
						timeout: 1000
					}
				]);
				//clean the dirty state
				this.setDirtyState(false);
			}).catch(error => {
				this.isSaving = false;
				window.EventBus.$emit('system-messages', [
					{ message: error.message, messageType: 'error' }
				]);
			});
		},
	}
};

function EditModule(editIndex, channel, extraData, getModuleHtmlText) {
	const module = channel.modules[editIndex];
	this.editIndex = editIndex;
	this.id = module.id;
	this.name = module.name;
	this.type = module.type;
	this.settings = { ...module.settings };
	this.moduleHtmlText = module.id
			? `<div class="bwmodule" data-bwmoduleid="${module.id}"></div>`
			: 'HTML code will become visible after the page is saved';

	const isSelected = (settings, itemId, itemType) => {
		return settings.total_displayed === -1 || !settings.sort_order_type_ids || settings.sort_order_type_ids.some(item =>
				item.type_id &&
				parseInt(item.type_id) === itemId &&
				item.type === itemType
		);
	}

	if(module.type === channel.const.TYPE_ITEM_CARDS) {
		this.selectedItems = extraData.items
				.filter(item => isSelected(this.settings, item.id, item.type))
				.map(item => ({ id: item.id, name: item.name, type: item.type, venueName: item.venue_name }));

		this.selectableItems = extraData.items
				.filter(item => !isSelected(this.settings, item.id, item.type))
				.map(item => ({ id: item.id, name: item.name, type: item.type }));

	}
	else if(module.type === channel.const.TYPE_CHECKOUT_HOW_IT_WORKS) {
		this.settings.title = this.settings.title || channel.const.TYPE_CHECKOUT_HOW_IT_WORKS_DEFAULT_SETTINGS.title;
		if(!this.settings.steps) {
			this.settings.steps = JSON.parse(JSON.stringify(channel.const.TYPE_CHECKOUT_HOW_IT_WORKS_DEFAULT_SETTINGS.steps));
		}
	}

}

</script>
<style lang="scss">
form#embedded,
form#module {
	.sortables > span {
		min-height: 200px;
		min-width: 100%;
		display: block;
	}
}
</style>
